import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Route, useLocation, Routes, Navigate, useNavigate, useMatch } from 'react-router-dom';
import * as DatePicker from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store/redux';
import { LoginState, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import { authenticationServiceFactory } from 'services/authenticationService';
import { telemetry } from './services/telemery';
import { IUserInRole } from 'pages/Login';
import {
  Home,
  Help,
  Statistics,
  Orders,
  Invoices,
  Conversations,
  Safetycheck,
  Files,
  Contact,
  Order,
  Login,
  SsoPage,
  InvoiceDetails,
} from 'pages';
import { Layout, AccessTokenProvider, LogoutConfirmationModal, ProfilePanel } from 'components';
import './custom.css';

DatePicker.registerLocale('sv', sv);
DatePicker.setDefaultLocale('sv');

export type AppProps = Partial<typeof AuthenticationStore.actions> & {
  userState?: UserState;
};

const ACTIVITY_STORAGE_KEY = 'last_activity_timestamp';
const LOGOUT_STORAGE_KEY = 'force_logout';

const App = function ({ userState, setLoginState, hideUserProfile }: AppProps) {
  const { i18n, t } = useTranslation('translation');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [title, setTitle] = useState('Avonova Portal');
  const [pageTrackingDetails, setPageTrackingDetails] = useState<any>();
  const authenticationState = userState;
  const [profilePanelIsVisible, setProfilePanelIsVisible] = useState(false);
  const isLoggedIn = userState ? userState.LoginState === LoginState.LOGGED_IN : false;
  const selectedCustomer = userState?.UserInRole?.selectedCustomer;
  const customerHasBeenSelected = !!selectedCustomer;
  const isConversationLink = useMatch('/contact/conversation/:conversationId');

  const logoutTime = 1800;
  const showModalTime = logoutTime - 1620;
  const [timer, setTimer] = useState(logoutTime);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const lastActivityRef = useRef(Date.now());

  const authenticationService = useMemo(() => {
    return authenticationServiceFactory(Context.APIUrl, { setLoginState }, userState);
  }, [userState]);

  const logOut = useCallback(() => {
    hideUserProfile && hideUserProfile();
    authenticationService.logoutUser();

    localStorage.setItem(LOGOUT_STORAGE_KEY, Date.now().toString());
    localStorage.removeItem('persist:root');

    window.location.replace('/login');
  }, [hideUserProfile, setLoginState, authenticationState]);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
    setTimer(logoutTime);
    setIsOpenModal(false);
  }, []);

  // Sync logout timer across tabs using localStorage storage event
  useEffect(() => {
    if (!isLoggedIn) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
        logOut();
      } else if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
        lastActivityRef.current = parseInt(event.newValue, 10);
        setTimer(logoutTime);
        setIsOpenModal(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check if there was recent activity in another tab
    const lastActivity = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (lastActivity) {
      const ts = parseInt(lastActivity, 10);
      lastActivityRef.current = ts;
      const elapsed = Math.floor((Date.now() - ts) / 1000);
      if (elapsed < logoutTime) {
        setTimer(logoutTime - elapsed);
      }
    }

    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isLoggedIn, hideUserProfile, setLoginState, authenticationState, logoutTime]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, logoutTime - elapsed);

      if (remaining <= 0) {
        logOut();
        return;
      }

      if (remaining <= showModalTime) {
        setIsOpenModal(true);
      }

      setTimer(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoggedIn, showModalTime, logOut, logoutTime]);

  useEffect(() => {
    if (!isLoggedIn || isOpenModal) return;

    const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isLoggedIn, isOpenModal, resetTimer]);

  useEffect(() => {
    return function cleanup() {
      if (pageTrackingDetails) {
        console.debug('page tracking stop');
        telemetry.stopTrackPage(
          pageTrackingDetails.title,
          pageTrackingDetails.url,
          pageTrackingDetails
        );
      }
    };
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      setTitle(selectedCustomer.customerName + ' (' + selectedCustomer.customerId + ')');
    } else {
      setTitle('Avonova Portal');
    }
  }, [selectedCustomer]);

  useEffect(() => {
    var match = pathname.match(/^\/(\d+)(\/.*|$)/);
    if (match) {
      var matchedId = match[1];
      if (userState && userState.UserInRole && userState?.LoginState === LoginState.LOGGED_IN) {
        if (userState.UserInRole?.role === 'avonovaAdmin') {
          authenticationService
            .getOrRenewNativeToken()
            .then((accessToken) => authenticationService.fetchUserDetails(accessToken, matchedId))
            .then((userInRole: IUserInRole) => {
              setLoginState &&
                setLoginState({
                  UserInRole: userInRole,
                });
            });
        } else {
          var customer = !!matchedId
            ? userState.UserInRole?.availableCustomers?.find(
                (c) => c.customerId.toString() === matchedId
              )
            : null;
          if (customer) {
            setLoginState &&
              setLoginState({
                UserInRole: {
                  ...userState.UserInRole,
                  selectedCustomer: customer,
                },
              });
          }
        }
      } else {
        setLoginState &&
          setLoginState({
            RequestedCustomerId: matchedId,
          });
      }
      if (userState?.LoginState !== LoginState.UNDEFINED) {
        console.debug('navigating to', match[2]);
        navigate(match[2], { replace: true });
      }
    }
  }, [pathname, setLoginState, userState?.LoginState, userState?.UserInRole?.selectedCustomer]);

  useEffect(() => {
    const _isLoggedIn = userState ? userState.LoginState === LoginState.LOGGED_IN : false;
    if (pathname === '/login' && _isLoggedIn) {
      navigate('/service');
    }
  }, [pathname, navigate, userState]);

  useEffect(() => {
    if (pageTrackingDetails) {
      telemetry.stopTrackPage(
        pageTrackingDetails.title,
        pageTrackingDetails.url,
        pageTrackingDetails
      );
    }
    telemetry.startTrackPage();
    const props: any = { url: document.location.href, title: document.title };
    setPageTrackingDetails(props);
  }, [pathname]);

  useEffect(() => {
    DatePicker.setDefaultLocale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (isLoggedIn && !customerHasBeenSelected && !isConversationLink) {
      setProfilePanelIsVisible(true);
    } else {
      setProfilePanelIsVisible(!!userState?.UserPanelIsVisible);
    }
  }, [isLoggedIn, customerHasBeenSelected, userState?.UserPanelIsVisible]);

  const checkTokenExpiration = useCallback(() => {
    if (!isLoggedIn || !userState) return;

    const now = new Date();
    const accessTokenExpired = userState.AccessTokenExpirationDate
      ? new Date(userState.AccessTokenExpirationDate) < now
      : true;
    const refreshTokenExpired = userState.RefreshTokenExpirationDate
      ? new Date(userState.RefreshTokenExpirationDate) < now
      : true;

    if (accessTokenExpired && refreshTokenExpired) {
      console.debug('Tokens expired, logging out user');
      logOut();
    }
  }, [isLoggedIn, userState, logOut]);

  // Check token expiration and inactivity when tab regains focus
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkOnResume = () => {
      checkTokenExpiration();

      // Immediately recalculate inactivity timer from the real clock
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, logoutTime - elapsed);

      if (remaining <= 0) {
        logOut();
        return;
      }

      if (remaining <= showModalTime) {
        setIsOpenModal(true);
      }

      setTimer(remaining);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkOnResume();
      }
    };

    const handleFocus = () => {
      checkOnResume();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoggedIn, checkTokenExpiration, logOut, showModalTime, logoutTime]);

  const activeConfirmation = () => {
    resetTimer();
  };

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {isLoggedIn ? (
        <>
          <AccessTokenProvider>
            <Routes>
              {selectedCustomer?.reportServiceIsAvailable && (
                <Route path="/service/statistics" element={<Statistics />} />
              )}
              {selectedCustomer?.orderServiceIsAvailable && (
                <>
                  <Route path="/service/order/:type/:id/copy" element={<Order />} />
                  <Route path="/service/order/:type/:id" element={<Order />} />
                  <Route path="/service/order/:type" element={<Order />} />
                  <Route path="/service/order" element={<Orders />} />
                </>
              )}
              {selectedCustomer?.invoicesAreAvailable && (
                <>
                  <Route path="/service/invoices" element={<Invoices />} />
                  <Route path="/service/invoices/:id" element={<InvoiceDetails />} />
                </>
              )}
              <Route path="/service" element={<Home />} />
              <Route path="/filearea" element={<Files />} />
              <Route path="/checklists" element={<Safetycheck />} />
              <Route path="/contact/conversation/:conversationId" element={<Conversations />} />
              <Route path="/contact/conversation" element={<Conversations />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/logout" element={<Home />} />
              <Route path="/login/resetpassword" element={<Help />} />
              <Route path="/sso/:id" element={<SsoPage />} />
              <Route path="*" element={<Navigate replace to="/service" />} />
            </Routes>
          </AccessTokenProvider>
          <ProfilePanel
            open={profilePanelIsVisible}
            onClose={() => hideUserProfile && hideUserProfile()}
            onLogout={logOut}
            authenticationState={authenticationState}
          />
          <LogoutConfirmationModal
            open={isOpenModal}
            onClose={logOut}
            onConfirm={activeConfirmation}
            remaining={timer}
          />
        </>
      ) : (
        <Routes>
          <Route path="/sso/:id" element={<SsoPage />} />
          <Route path="*" element={<Login />}></Route>
        </Routes>
      )}
    </Layout>
  );
};

export default connect(
  (state: ApplicationState, ownProps: AppProps) => ({
    userState: state.authentication,
  }),
  AuthenticationStore.actions
)(App);
